import React, { useRef, useContext, forwardRef } from 'react';
import propTypes from 'prop-types';
import { DialogProvider } from '../Dialog';
import { QuoteProvider, QuoteContext } from './QuoteProvider';
import { QuantityProvider } from '../QuantitySelector/QuantityProvider';
import { QuoteTotal } from './QuoteTotal';
import { TireCards } from './TireCards';
import { PrintQuote } from './PrintQuote';
import { InstallationOptions } from './InstallationOptions/InstallationOptions';
import { HazardWarranty } from './Addons/HazardWarranty';
import { TPMSServiceKit } from './Addons/TPMSServiceKit';
import { Deals } from './Deals';
import { productPropTypes } from './productPropTypes';
import { StoreInformation } from './StoreInformation/StoreInformation';
import { QuoteHeading } from './QuoteHeading';
import { QuoteDisclaimer } from './QuoteDisclaimer';
import { LoadingAnimation } from './LoadingAnimation';
import { ErrorModal } from './ErrorModal';

const QuoteWrapper = forwardRef(({ children }, quoteRef) => {
  const { loadState, loadingState } = useContext(QuoteContext);

  return (
    <div
      className={`cmp-quote ${loadingState === loadState.FINALIZING ? 'cmp-quote--loading' : ''}`}
      ref={quoteRef}
    >
      {children}
    </div>
  );
});

QuoteWrapper.propTypes = {
  children: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node,
    propTypes.string,
  ]),
};

const Quote = ({
  storeDetails,
  vehicleInformation,
  product,
  product2,
  isSplitFitment,
  standardInstallation,
  economyInstallation,
  trailerTireInstallation,
  pricing,
  isTrailerTire,
}) => {
  const quoteRef = useRef();
  // Boolean toggles TMPS / Road Hazard options
  const showWarranties = false;

  return (
    <DialogProvider>
      <QuantityProvider>
        <QuoteProvider
          installationType={isTrailerTire ? trailerTireInstallation : standardInstallation}
        >
          <QuoteWrapper ref={quoteRef}>
            <QuoteHeading
              vehicleInformation={vehicleInformation}
              product={product}
              product2={product2}
            />

            <TireCards
              pricing={pricing}
              product={product}
              product2={product2}
              isSplitFitment={isSplitFitment}
            />

            {(product?.deals?.length > 0 && isTrailerTire === false) && (
              <Deals
                deals={product.deals}
                isTrailerTire={isTrailerTire}
                product={product}
              />
            )}

            <InstallationOptions
              standardInstallation={standardInstallation}
              economyInstallation={economyInstallation}
              trailerTireInstallation={trailerTireInstallation}
              pricing={pricing}
              isSplitFitment={isSplitFitment}
              isTrailerTire={isTrailerTire}
            />

            {showWarranties && !isTrailerTire && (
              <TPMSServiceKit isSplitFitment={isSplitFitment} />
            )}

            {!isTrailerTire && (
              <HazardWarranty
                pricing={pricing}
                product={product}
                product2={product2}
                isSplitFitment={isSplitFitment}
              />
            )}

            <QuoteTotal
              product={product}
              product2={product2}
              storeDetails={storeDetails}
              isSplitFitment={isSplitFitment}
              isTrailerTire={isTrailerTire}
            />

            <StoreInformation
              storeDetails={storeDetails}
              product={product}
              product2={product2}
            />

            <PrintQuote />

            <QuoteDisclaimer />
            <LoadingAnimation ref={quoteRef} />
            <ErrorModal storeDetails={storeDetails} />
          </QuoteWrapper>
        </QuoteProvider>
      </QuantityProvider>
    </DialogProvider>
  );
};

Quote.propTypes = {
  isSplitFitment: propTypes.bool,
  product: productPropTypes,
  product2: productPropTypes,
  storeDetails: propTypes.shape({
    number: propTypes.string,
    name: propTypes.string,
    phone: propTypes.string,
    phoneVisual: propTypes.string,
    phoneAccessible: propTypes.string,
    storeLink: propTypes.string,
    distanceMiles: propTypes.number,
    pricingGroup: propTypes.string,
    yelpUrl: propTypes.string,
    facebookUrl: propTypes.string,
    state: propTypes.string,
  }),
  vehicleInformation: propTypes.shape({
    splitFitment: propTypes.bool,
    year: propTypes.number,
    make: propTypes.string,
    model: propTypes.string,
    option: propTypes.string,
    tireWidth: propTypes.string,
    tireRimSize: propTypes.string,
    tireAspectRatio: propTypes.string,
    tireString: propTypes.string,
  }),
  standardInstallation: propTypes.shape({
    id: propTypes.number,
    productType: propTypes.string,
    sku: propTypes.string,
  }),
  economyInstallation: propTypes.shape({
    id: propTypes.number,
    productType: propTypes.string,
    sku: propTypes.string,
  }),
  trailerTireInstallation: propTypes.shape({
    id: propTypes.number,
    productType: propTypes.string,
    sku: propTypes.string,
  }),
  pricing: propTypes.shape({
    standardInstallation: propTypes.number,
    economyInstallation: propTypes.number,
    trailerTireInstallation: propTypes.number,
  }),
  isTrailerTire: propTypes.bool,
};

export { Quote };
